import { useEffect, useState } from "react";
import { GitPullRequest, Check, Loader2 } from "lucide-react";
import { BuildingScreen } from "../evolution/BuildingScreen";
import { fetchEvolutionProposals } from "../../lib/evolution";

// Stage 2a — executable vs intent-captured. A schema-mutation card is runnable
// only with a non-empty schema_diff (the generator empties it for intent-captured
// cards); self-executing types have their own executors and need no schema_diff;
// other/rename_screen/remove_screen have no executor → intent-captured.
const SELF_EXECUTING_TYPES = new Set(["assignment_proposal", "agent_deployment", "schema_standard_upgrade"]);
const REQUIRED_DIFF_KEYS: Record<string, string[]> = {
  new_field: ["screen_id", "field_key"],
  new_screen: ["screen_id", "label"],
  rename_field: ["screen_id", "field_key"],
  remove_field: ["screen_id", "field_key"],
  reorder: ["order"],
  new_component: ["screen_id", "component_type"],
};

function isExecutableProposal(p: { type: string; schema_diff?: Record<string, any> | null }): boolean {
  if (SELF_EXECUTING_TYPES.has(p.type)) return true;
  if (!p.schema_diff || typeof p.schema_diff !== "object") return false;
  const reqs = REQUIRED_DIFF_KEYS[p.type];
  if (!reqs) return false;
  return reqs.every((key) => Boolean(p.schema_diff?.[key]));
}

// EVOLUTION-1 — proposals come from os_evolution_proposals (the table the
// Operator writes to via propose_os_change). The legacy os_evolutions
// table is deprecated and no longer read here. Approve/dismiss + Studio
// review handoff ship in EVOLUTION-2 — for now, proposals display read-
// only with a "Pending review" badge.
interface EvolutionProposal {
  id: string;
  type: string;
  title: string;
  description: string | null;
  rationale: string;
  status: string;
  created_at: string;
  // EVOLUTION-3e — populated when the executor flips status to 'applied'.
  // Drives the timestamp on history cards.
  applied_at: string | null;
  // Stage 2a — drives executable-vs-intent-captured (already returned by the GET).
  schema_diff?: Record<string, any> | null;
}

type EvolvingProposal = { id: string; title: string; type: string };

export function EvolutionCenter({ workspaceId, onRefine }: { workspaceId: string; onRefine?: () => void }) {
  const [proposals, setProposals] = useState<EvolutionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  // EVOLUTION-3b — building overlay state, mirrors Operator.tsx.
  const [evolvingProposal, setEvolvingProposal] = useState<EvolvingProposal | null>(null);

  useEffect(() => {
    async function loadEvolutions() {
      setLoading(true);
      try {
        const data = await fetchEvolutionProposals({ workspaceId, statuses: ["pending", "approved", "applied"] });
        setProposals(data as EvolutionProposal[]);
      } catch (err) {
        console.warn("[EvolutionCenter] failed to load proposals:", err);
        setProposals([]);
      }
      setLoading(false);
    }
    loadEvolutions();
  }, [workspaceId]);

  // EVOLUTION-2 — approve/dismiss handler shared by both action buttons.
  // Optimistically removes the proposal from the pending list (it's no
  // longer pending once acted on, either way). On server failure, refetch
  // to restore an accurate view.
  //
  // EVOLUTION-3b — on successful approve, launches the BuildingScreen
  // overlay which drives the /execute SSE stream and shows progress.
  const handleProposalDecide = async (
    proposal_id: string,
    decision: "approve" | "dismiss",
    proposal_title?: string,
    proposal_type?: string,
  ) => {
    setProposals(prev => prev.filter(p => p.id !== proposal_id));
    try {
      const res = await fetch(`/api/evolution/proposals/${proposal_id}/${decision}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok && res.status !== 409) {
        console.warn(`[EvolutionCenter] proposal ${decision} failed:`, res.status);
        try {
          const data = await fetchEvolutionProposals({ workspaceId, statuses: ["pending", "approved", "applied"] });
          setProposals(data as EvolutionProposal[]);
        } catch (reloadErr) {
          console.warn("[EvolutionCenter] failed to refresh proposals:", reloadErr);
        }
        return;
      }
      // EVOLUTION-3b — on successful approve, show building screen.
      if (decision === "approve" && proposal_title) {
        setEvolvingProposal({ id: proposal_id, title: proposal_title, type: proposal_type || "" });
      }
    } catch (err) {
      console.warn(`[EvolutionCenter] proposal ${decision} error:`, err);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mb-4" />
        <p>Checking for architectural proposals...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="mb-8 border-b border-slate-200 pb-6">
        <h1 className="text-3xl font-light text-slate-900 flex items-center gap-3">
          <GitPullRequest className="text-indigo-500 w-8 h-8" /> Evolution Center
        </h1>
        <p className="text-slate-500 mt-2">
          Your Agentic Operating System analyzes your usage and proposes structural upgrades, standing orders, and staff requests.
          Nothing changes without your explicit approval.
        </p>
      </div>

      {/* EVOLUTION-3e — split into pending (actionable) and applied
          (history). Decline'd entries are filtered out by the query. */}
      {(() => {
        const pending = proposals.filter(p => p.status === "pending" || p.status === "approved");
        const applied = proposals
          .filter(p => p.status === "applied")
          .sort((a, b) =>
            (b.applied_at || b.created_at).localeCompare(a.applied_at || a.created_at)
          );

        // Empty state only when BOTH lists are empty.
        if (pending.length === 0 && applied.length === 0) {
          return (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-slate-100">
                <Check className="text-emerald-500 w-8 h-8" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-1">Your Agentic Operating System is fully optimized</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                Your Agentic Operating System has no pending proposals at this time. Use your Agentic Operating System normally, and check back later for new structural recommendations.
              </p>
            </div>
          );
        }

        return (
          <>
            {pending.length > 0 && (
              <div className="flex flex-col gap-6">
                {pending.map((proposal) => (
                  <div key={proposal.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 px-2 py-0.5 bg-indigo-50 rounded">
                            {proposal.type.replace(/_/g, " ")}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(proposal.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">{proposal.title}</h3>
                      </div>
                    </div>

                    {proposal.description && (
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">{proposal.description}</p>
                    )}

                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl mb-6">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Why
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {proposal.rationale}
                      </p>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => handleProposalDecide(proposal.id, "dismiss")}
                        className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium border border-slate-300 transition-colors"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => {
                          if (proposal.status === "approved") {
                            setProposals(prev => prev.filter(p => p.id !== proposal.id));
                            setEvolvingProposal({ id: proposal.id, title: proposal.title, type: proposal.type });
                            return;
                          }
                          handleProposalDecide(proposal.id, "approve", proposal.title, proposal.type);
                        }}
                        className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
                      >
                        {proposal.status === "approved"
                          ? proposal.type === "agent_deployment" ? "Add to sidebar" : "Apply approved"
                          : proposal.type === "assignment_proposal"
                          ? "Schedule it"
                          : proposal.type === "agent_deployment"
                            ? "Approve request"
                            : "Yes, evolve"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {applied.length > 0 && (
              <div className="mt-12">
                <div className="border-b border-slate-200 pb-4 mb-6">
                  <h2 className="text-lg font-medium text-slate-900">Evolution History</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    How your Agentic Operating System has changed over time.
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  {applied.map((proposal) => (
                    <div key={proposal.id} className="bg-slate-50/50 border border-slate-100 rounded-xl p-4">
                      <div className="flex items-baseline justify-between gap-3 mb-1.5">
                        <h3 className="text-sm font-semibold text-slate-900 truncate">{proposal.title}</h3>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {proposal.applied_at
                            ? new Date(proposal.applied_at).toLocaleDateString(undefined, {
                                month: "short", day: "numeric", year: "numeric",
                              })
                            : "—"}
                        </span>
                      </div>
                      {proposal.description && (
                        <p className="text-sm text-slate-700 leading-relaxed mb-1.5 line-clamp-2">
                          {proposal.description}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 leading-relaxed italic line-clamp-2">
                        {proposal.rationale}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}
      {/* EVOLUTION-3b — building overlay. SESSION-3 keeps the cookie 30
          days so the navigation doesn't bounce.
          EVOLUTION-3e — Show me navigates to /?screen=...&spotlight=...
          so SchemaRenderer can switch screens and Spotlight can anchor. */}
      {evolvingProposal && (
        <BuildingScreen
          proposalId={evolvingProposal.id}
          proposalTitle={evolvingProposal.title}
          onClose={() => setEvolvingProposal(null)}
          onApplied={(info) => {
            if (evolvingProposal.type === "agent_deployment") {
              window.location.href = "/";
              return;
            }
            const params = new URLSearchParams();
            if (info.target_screen_id) params.set("screen", info.target_screen_id);
            if (info.target_field_key) params.set("spotlight", info.target_field_key);
            const qs = params.toString();
            window.location.href = qs ? `/?${qs}` : "/";
          }}
        />
      )}
    </div>
  );
}
